import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Eye, 
  EyeOff, 
  Save, 
  X, 
  Upload,
  MessageSquare,
  Sparkles,
  Settings,
  CheckCircle,
  AlertCircle,
  Download,
  Layout,
  Copy
} from 'lucide-react';

const LandingPagePopups = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('list');
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 
    (window.location.origin.includes("localhost") 
      ? "http://localhost:8000" 
      : window.location.origin + "/pandvah-backend");

  useEffect(() => {
    if (activeTab === 'list') {
      fetchPopups();
    }
  }, [activeTab]);

  const fetchPopups = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/popups/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setPopups(response.data.popups);
      }
    } catch (error) {
      console.error('Error fetching popups:', error);
      showMessage('error', 'Failed to load popups');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const tabs = [
    { id: 'generate', label: 'Generate', icon: Sparkles },
    { id: 'list', label: 'Popup List', icon: Settings }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Landing Page Popups
              </h2>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Message Display */}
            {message.text && (
              <div className={`mb-6 p-4 rounded-lg flex items-center ${
                message.type === 'success' 
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {message.type === 'success' ? (
                  <CheckCircle className="h-5 w-5 mr-3" />
                ) : (
                  <AlertCircle className="h-5 w-5 mr-3" />
                )}
                {message.text}
              </div>
            )}

            {/* Tab Navigation - Styled like Short URLs */}
            <div className="bg-white rounded-lg shadow-sm mb-6 border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                          isActive
                            ? 'border-theme-blue text-theme-blue'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'generate' && (
                  <GenerateTab 
                    API_BASE_URL={API_BASE_URL}
                    showMessage={showMessage}
                    onPopupCreated={() => {
                      setActiveTab('list');
                      fetchPopups();
                    }}
                  />
                )}
                
                {activeTab === 'list' && (
                  <PopupListTab 
                    popups={popups}
                    loading={loading}
                    API_BASE_URL={API_BASE_URL}
                    showMessage={showMessage}
                    onUpdate={fetchPopups}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Generate Tab Component - Full Implementation
const GenerateTab = ({ API_BASE_URL, showMessage, onPopupCreated }) => {
  const [mode, setMode] = useState('ai'); // 'ai' or 'manual'
  const [popupName, setPopupName] = useState('');
  const [popupTitle, setPopupTitle] = useState('');
  const [popupDescription, setPopupDescription] = useState('');
  const [popupImage, setPopupImage] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [popupLayout, setPopupLayout] = useState('left'); // 'left', 'right', 'background', 'text_only'
  const [imageFormat, setImageFormat] = useState('portrait'); // 'portrait' or 'square' - format for left/right/background layouts
  const [imageSource, setImageSource] = useState('upload'); // 'ai_generate', 'upload', 's3_browse'
  const [imageOpacity, setImageOpacity] = useState(100); // 0-100 for background opacity
  const [s3Assets, setS3Assets] = useState([]);
  const [showS3Browser, setShowS3Browser] = useState(false);
  const [formFields, setFormFields] = useState([
    { field_type: 'text', label: 'Name', placeholder: 'Your name', required: true, order: 0 }
  ]);
  const [buttonText, setButtonText] = useState('Submit');
  const [size, setSize] = useState('medium');
  const [buttonColor, setButtonColor] = useState('var(--theme-primary)');
  const [saving, setSaving] = useState(false);
  
  // Banner text states
  const [enableCustomBannerText, setEnableCustomBannerText] = useState(false);
  const [customBannerText, setCustomBannerText] = useState('[BUSINESS_NAME] [PHONE_NUMBER]');
  
  // Popup background styling
  const [popupBackgroundColor, setPopupBackgroundColor] = useState('#ffffff');
  const [popupBackgroundOpacity, setPopupBackgroundOpacity] = useState(100);
  
  // AI Generation states
  const [aiPrompt, setAiPrompt] = useState('');
  const [generatingTemplate, setGeneratingTemplate] = useState(null); // Track which template is generating
  const [showTemplates, setShowTemplates] = useState(true);
  const [showGeneratingModal, setShowGeneratingModal] = useState(false); // New modal for generating
  const [showGeneratingImageModal, setShowGeneratingImageModal] = useState(false); // Modal for image generation
  const [templates, setTemplates] = useState([]); // User's saved templates
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // State for image generation modal (status and message)
  const [generatingModalStatus, setGeneratingModalStatus] = useState('generating'); // 'generating', 'success', 'error'
  const [generatingModalMessage, setGeneratingModalMessage] = useState('');

  const [showPreviewModal, setShowPreviewModal] = useState(false); // Preview modal
  
  // Image model selection states
  const [availableImageModels, setAvailableImageModels] = useState([]);
  const [selectedImageModel, setSelectedImageModel] = useState('');
  const [useOpenAI, setUseOpenAI] = useState(false);
  
  // Popup formats from AI Settings
  const [popupFormats, setPopupFormats] = useState({});
  
  const templatePrompts = [
    {
      type: 'discount',
      title: '10% Discount Offer',
      description: 'Get 10% off your next visit',
      prompt: 'Create a promotional popup offering 10% discount on next veterinary visit'
    },
    {
      type: 'newsletter',
      title: 'Newsletter Signup',
      description: 'Stay updated with pet care tips',
      prompt: 'Create a newsletter signup popup for veterinary practice with pet care tips'
    },
    {
      type: 'appointment',
      title: 'Book Appointment',
      description: 'Schedule your pet\'s checkup',
      prompt: 'Create an appointment booking popup for veterinary clinic'
    },
    {
      type: 'general',
      title: 'Custom Offer',
      description: 'Create your own promotion',
      prompt: ''
    }
  ];
  
  // Fetch available image models on component mount
  useEffect(() => {
    const fetchImageModels = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        
        // Fetch OpenAI settings to check if override is enabled
        const openaiResponse = await axios.get(`${API_BASE_URL}/api/openai-settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const openaiData = openaiResponse.data;
        const isOpenAIEnabled = openaiData?.settings?.override_llm === true;
        setUseOpenAI(isOpenAIEnabled);
        
        if (isOpenAIEnabled) {
          // Use OpenAI image models - convert from {value, label} to {id, name}
          const imageModelsData = (openaiData.image_models || []).map(model => ({
            id: model.value || model.id,
            name: model.label || model.name
          }));
          setAvailableImageModels(imageModelsData);
          
          // Set default from settings
          const defaultImageModel = openaiData.settings.image_generation_model || 'dall-e-3';
          setSelectedImageModel(defaultImageModel);
          
          console.log('🖼️ Loaded image models:', imageModelsData);
          console.log('🎯 Default image model:', defaultImageModel);
        } else {
          // Use OpenRouter image models
          const openRouterResponse = await axios.get(`${API_BASE_URL}/api/openrouter-models`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (openRouterResponse.data.available_models) {
            const imageModels = Object.entries(openRouterResponse.data.available_models)
              .filter(([_, modelInfo]) => modelInfo.category === 'image_generation')
              .map(([modelId, modelInfo]) => ({
                id: modelId,
                name: modelInfo.name
              }));
            
            setAvailableImageModels(imageModels);
            
            // Set default if available
            if (imageModels.length > 0) {
              setSelectedImageModel(imageModels[0].id);
            }
            
            console.log('🖼️ Loaded OpenRouter image models:', imageModels);
          }
        }
      } catch (error) {
        console.error('Error fetching image models:', error);
        // Fallback defaults including Nano Banana
        setAvailableImageModels([
          { id: 'dall-e-3', name: 'DALL-E 3' },
          { id: 'gpt-image-1', name: 'GPT Image 1' },
          { id: 'google/gemini-2.5-flash-image-preview', name: 'Nano Banana (Google Gemini)' }
        ]);
        setSelectedImageModel('dall-e-3');
      }
    };
    
    fetchImageModels();
  }, [API_BASE_URL]);
  
  // Fetch user's templates
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/api/popup-templates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.data.success) {
          setTemplates(response.data.templates);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };
    
    if (mode === 'ai') {
      fetchTemplates();
    }
  }, [API_BASE_URL, mode]);
  
  // Auto-adjust image format when layout changes
  useEffect(() => {
    if (popupLayout === 'background') {
      // For background layout, default to landscape if portrait is selected
      // But allow square to remain as square
      if (imageFormat === 'portrait') {
        setImageFormat('landscape');
      }
    } else if (popupLayout === 'left' || popupLayout === 'right') {
      // Default to portrait for left/right layouts
      if (!imageFormat || imageFormat === 'landscape') {
        setImageFormat('portrait');
      }
    }
  }, [popupLayout]);

  // Template creation modal states
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [templateDescription, setTemplateDescription] = useState('');
  const [generatingTemplateStructure, setGeneratingTemplateStructure] = useState(false);
  const [templateError, setTemplateError] = useState('');
  
  // Edit template modal states
  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editTemplateName, setEditTemplateName] = useState('');
  
  const fieldTypes = [
    { value: 'text', label: 'Text Field' },
    { value: 'email', label: 'Email Field' },
    { value: 'phone', label: 'Phone Field' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'checkbox', label: 'Checkbox' }
  ];

  const handleAIGenerate = async (templateType, customPrompt = '') => {
    setGeneratingTemplate(templateType);
    setShowGeneratingModal(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/popups/ai-generate`,
        {
          prompt: customPrompt || aiPrompt,
          template_type: templateType
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        const generated = response.data.generated_popup;
        setPopupTitle(generated.title);
        setPopupDescription(generated.description);
        setButtonText(generated.button_text);
        setFormFields(generated.form_fields || formFields);
        showMessage('success', 'Popup content generated successfully!');
        setShowTemplates(false);
      }
    } catch (error) {
      console.error('AI generation error:', error);
      if (error.code === 'ERR_NETWORK') {
        showMessage('error', 'Network error - please check your connection and try again');
      } else if (error.response) {
        showMessage('error', error.response.data?.detail || 'Failed to generate popup content');
      } else {
        showMessage('error', 'Failed to generate popup content - network issue');
      }
    } finally {
      setGeneratingTemplate(null);
      setShowGeneratingModal(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    setImageFile(file);
    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPopupImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const fetchS3Assets = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/s3/list-assets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        // Filter for images only
        const imageAssets = response.data.assets.filter(asset => 
          asset.file_type && asset.file_type.startsWith('image/')
        );
        setS3Assets(imageAssets);
      }
    } catch (error) {
      console.error('Error fetching S3 assets:', error);
      showMessage('error', 'Failed to load images');
    }
  };

  const handleS3ImageSelect = (assetUrl) => {
    // Check if we're in edit mode or create mode
    if (editModal.show) {
      setEditImage(assetUrl);
      setEditImageFile(null);
    } else {
      setPopupImage(assetUrl);
      setImageFile(null);
    }
    setShowS3Browser(false);
  };

  const handleAIImageGenerate = async () => {
    if (!popupTitle && !popupDescription) {
      showMessage('error', 'Please provide popup title or description first');
      return;
    }
    
    try {
      setShowGeneratingImageModal(true);
      setGeneratingModalStatus('generating');
      setGeneratingModalMessage('');
      
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Fetch AI settings to get image model
      let imageModel = 'google/gemini-2.5-flash-image-preview'; // default to Nano Banana
      
      try {
        const settingsResponse = await axios.get(`${API_BASE_URL}/api/ai-settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (settingsResponse.data) {
          // Use the selected image model from component state (user selection or default from settings)
          imageModel = selectedImageModel || settingsResponse.data.image_model || 'google/gemini-2.5-flash-image-preview';
        }
      } catch (error) {
        console.error('Error fetching AI settings:', error);
      }
      
      const imagePrompt = `Create a professional marketing background image for a popup promotion: ${popupTitle}. ${popupDescription}. Modern, clean, eye-catching design. NO text in the image. Background style suitable for overlaying text.`;
      
      console.log('🎨 [CREATE MODE] Image Generation Debug:');
      console.log('  Layout:', popupLayout);
      console.log('  Selected Format:', imageFormat);
      console.log('  Image Model:', imageModel);
      console.log('  Selected Model:', selectedImageModel);
      console.log('  Enable Custom Banner:', enableCustomBannerText);
      console.log('  Custom Banner Text:', enableCustomBannerText ? customBannerText : 'N/A');
      
      const response = await axios.post(
        `${API_BASE_URL}/api/ai/generate-image`,
        {
          prompt: imagePrompt,
          model: imageModel,
          platform: 'popup',  // Send platform to get AI settings
          format: imageFormat,  // Send the actual selected format: 'portrait', 'square', or 'landscape'
          enable_custom_image_text: enableCustomBannerText,  // Pass banner text flag
          custom_text: enableCustomBannerText ? customBannerText : null  // Pass banner text if enabled
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      console.log('Image generation response:', response.data);

      if (response.data.image_url) {
        setPopupImage(response.data.image_url);
        setImageFile(null);
        
        // Show success in modal
        setGeneratingModalStatus('success');
        setGeneratingModalMessage('AI image generated successfully!');
        
        // Auto-close after 2 seconds
        setTimeout(() => {
          setShowGeneratingImageModal(false);
          setGeneratingModalStatus('generating');
          setGeneratingModalMessage('');
        }, 2000);
      } else {
        console.error('No image_url in response:', response.data);
        setGeneratingModalStatus('error');
        setGeneratingModalMessage('Failed to generate AI image');
      }
    } catch (error) {
      console.error('Error generating AI image:', error);
      
      let errorMessage = 'Failed to generate AI image - please try again';
      if (error.code === 'ERR_NETWORK' || error.message.includes('network')) {
        errorMessage = 'Network error - please check your connection and try again';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setGeneratingModalStatus('error');
      setGeneratingModalMessage(errorMessage);
    }
  };

  const addFormField = () => {
    setFormFields([...formFields, {
      field_type: 'text',
      label: 'New Field',
      placeholder: '',
      required: true,
      order: formFields.length
    }]);
  };

  const updateFormField = (index, updates) => {
    const updated = [...formFields];
    updated[index] = { ...updated[index], ...updates };
    setFormFields(updated);
  };

  const removeFormField = (index) => {
    setFormFields(formFields.filter((_, i) => i !== index));
  };

  const moveFieldUp = (index) => {
    if (index === 0) return;
    const updated = [...formFields];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    updated.forEach((field, i) => field.order = i);
    setFormFields(updated);
  };

  const moveFieldDown = (index) => {
    if (index === formFields.length - 1) return;
    const updated = [...formFields];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    updated.forEach((field, i) => field.order = i);
    setFormFields(updated);
  };

  const uploadImageToS3 = async () => {
    if (!imageFile) return popupImage;
    
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', imageFile);
      formData.append('folder', 'landing-page-popups');

      const response = await axios.post(
        `${API_BASE_URL}/api/s3/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      return response.data.url;
    } catch (error) {
      console.error('Image upload error:', error);
      throw new Error('Failed to upload image');
    }
  };

  const handleSavePopup = async () => {
    if (!popupName.trim()) {
      showMessage('error', mode === 'manual' ? 'Please enter a template name' : 'Please enter a popup name');
      return;
    }
    
    // Only require title for AI mode (popups), not for manual mode (templates)
    if (mode === 'ai' && !popupTitle.trim()) {
      showMessage('error', 'Please enter a popup title');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      if (mode === 'manual') {
        // Save as TEMPLATE (not popup) - exclude title, description, image
        const response = await axios.post(
          `${API_BASE_URL}/api/popup-templates/create`,
          {
            name: popupName,
            configuration: {
              layout: popupLayout,
              image_format: imageFormat,
              form_fields: formFields,
              size: size,
              position: 'center',
              close_button_behavior: 'always',
              close_button_delay: 0,
              button_text: buttonText,
              button_color: buttonColor,
              background_color: popupBackgroundColor,
              background_opacity: popupBackgroundOpacity,
              text_color: '#000000'
            }
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          showMessage('success', 'Template created successfully!');
          // Reset form
          setPopupName('');
          setPopupTitle('');
          setPopupDescription('');
          setPopupImage('');
          setImageFile(null);
          setFormFields([]);
          setButtonText('Submit');
          setButtonColor('var(--theme-primary)');
          setPopupBackgroundColor('#ffffff');
          setPopupBackgroundOpacity(100);
        } else {
          showMessage('error', response.data.message || 'Failed to create template');
        }
      } else {
        // AI mode - save as popup with all content
        // Upload image if exists
        let imageUrl = popupImage;
        if (imageFile) {
          imageUrl = await uploadImageToS3();
        }

        const response = await axios.post(
          `${API_BASE_URL}/api/popups/create`,
          {
            name: popupName,
            configuration: {
              title: popupTitle,
              description: popupDescription,
              image_url: imageUrl,
              image_opacity: imageOpacity,
              layout: popupLayout,
              image_format: imageFormat, // Save image format (portrait or square)
              image_source: imageSource, // Save which method was used to add the image
              form_fields: formFields,
              size: size,
              position: 'center',
              close_button_behavior: 'always',
              close_button_delay: 0,
              button_text: buttonText,
              button_color: buttonColor,
              background_color: popupBackgroundColor,
              background_opacity: popupBackgroundOpacity,
              text_color: '#000000',
              enable_custom_banner_text: enableCustomBannerText,
              custom_banner_text: customBannerText
            },
            ai_generated: mode === 'ai',
            ai_prompt: aiPrompt
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          showMessage('success', 'Popup created successfully!');
          // Reset form
          setPopupName('');
          setPopupTitle('');
          setPopupDescription('');
          setPopupImage('');
          setImageFile(null);
          setImageOpacity(100);
          setPopupLayout('left');
          setImageSource('upload');
          setAiPrompt('');
          setShowTemplates(true);
          onPopupCreated();
        } else {
          showMessage('error', response.data.message || 'Failed to create popup');
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      const errorMessage = mode === 'manual' ? 'Failed to create template' : 'Failed to create popup';
      showMessage('error', errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateTemplate = async () => {
    if (!templateDescription.trim()) {
      showMessage('error', 'Please describe the template');
      return;
    }

    setGeneratingTemplateStructure(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Step 1: Generate template structure using AI
      const aiResponse = await axios.post(
        `${API_BASE_URL}/api/popup-templates/ai-generate`,
        { description: templateDescription },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!aiResponse.data.success) {
        throw new Error('Failed to generate template structure');
      }

      const templateStructure = aiResponse.data.template;
      
      // Step 2: Generate AI image based on image_prompt
      const imageResponse = await axios.post(
        `${API_BASE_URL}/api/ai/generate-image`,
        {
          prompt: templateStructure.image_prompt,
          platform: 'popup',
          format: templateStructure.image_format || 'portrait',
          model: selectedImageModel
        },
        { 
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 120000
        }
      );

      const imageUrl = imageResponse.data.image_url;

      // Step 3: Save complete template with generated image
      const saveResponse = await axios.post(
        `${API_BASE_URL}/api/popup-templates/create`,
        {
          name: templateStructure.name,
          configuration: {
            title: templateStructure.title,
            description: templateStructure.description,
            image_url: imageUrl,
            layout: templateStructure.layout,
            image_format: templateStructure.image_format || 'portrait',
            form_fields: templateStructure.form_fields,
            size: templateStructure.size || 'medium',
            position: 'center',
            close_button_behavior: 'always',
            close_button_delay: 0,
            button_text: templateStructure.button_text,
            button_color: templateStructure.button_color || 'var(--theme-primary)',
            background_color: templateStructure.background_color || '#ffffff',
            background_opacity: templateStructure.background_opacity || 100,
            text_color: '#000000'
          }
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (saveResponse.data.success) {
        showMessage('success', 'Template created successfully!');
        setShowCreateTemplateModal(false);
        setTemplateDescription('');
        
        // Refresh templates list
        const templatesResponse = await axios.get(`${API_BASE_URL}/api/popup-templates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (templatesResponse.data.success) {
          setTemplates(templatesResponse.data.templates);
        }
      }
    } catch (error) {
      console.error('Error generating template:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to generate template';
      setTemplateError(errorMessage);
      // Auto-clear error after 5 seconds
      setTimeout(() => setTemplateError(''), 5000);
    } finally {
      setGeneratingTemplateStructure(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setEditTemplateName(template.name);
    setShowEditTemplateModal(true);
  };

  const handleUpdateTemplate = async () => {
    if (!editTemplateName.trim()) {
      showMessage('error', 'Please enter a template name');
      return;
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/api/popup-templates/${editingTemplate.id}`,
        {
          name: editTemplateName,
          configuration: editingTemplate.configuration
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', 'Template updated successfully');
        setShowEditTemplateModal(false);
        setEditingTemplate(null);
        setEditTemplateName('');
        
        // Refresh templates list
        const templatesResponse = await axios.get(`${API_BASE_URL}/api/popup-templates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (templatesResponse.data.success) {
          setTemplates(templatesResponse.data.templates);
        }
      }
    } catch (error) {
      console.error('Error updating template:', error);
      showMessage('error', 'Failed to update template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.delete(
        `${API_BASE_URL}/api/popup-templates/${templateId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', 'Template deleted successfully');
        setTemplates(templates.filter(t => t.id !== templateId));
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      showMessage('error', 'Failed to delete template');
    }
  };

  const handleGeneratePopupFromTemplate = async (template) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Create popup directly from template
      const response = await axios.post(
        `${API_BASE_URL}/api/popups/create`,
        {
          name: `${template.name} - ${new Date().toLocaleDateString()}`,
          configuration: template.configuration,
          ai_generated: false
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', 'Popup created successfully!');
        onPopupCreated(); // Refresh popup list
      } else {
        showMessage('error', response.data.message || 'Failed to create popup');
      }
    } catch (error) {
      console.error('Error generating popup from template:', error);
      showMessage('error', 'Failed to create popup');
    }
  };

  return (
    <div className="space-y-6">
      {/* Mode Selection with Orange Active Color */}
      <div className="flex gap-2 border-b border-gray-200 pb-4">
        <button
          onClick={() => setMode('ai')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            mode === 'ai'
              ? 'text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          style={mode === 'ai' ? { backgroundColor: '#fb923c' } : {}}
        >
          <Sparkles className="inline h-4 w-4 mr-2" />
          AI Generate
        </button>
        <button
          onClick={() => setShowCreateTemplateModal(true)}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
        >
          <Edit3 className="inline h-4 w-4 mr-2" />
          Create New Template
        </button>
      </div>

      {/* AI Generation Mode - Display User Templates */}
      {mode === 'ai' && (
        <div>
          {/* Section Header with Grey Background */}
          <div className="bg-gray-100 px-4 py-3 rounded-lg mb-4">
            <h3 className="text-sm font-medium text-gray-700">Choose a Template to Generate Content</h3>
            <p className="text-xs text-gray-500 mt-1">Select a template and AI will generate title, description, and image</p>
          </div>
          
          {loadingTemplates ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Loading templates...</p>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
              <Layout className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <h3 className="text-base font-medium text-gray-900 mb-2">No Templates Yet</h3>
              <p className="text-sm text-gray-600 mb-4">Create your first template to start generating AI-powered popups</p>
              <button
                onClick={() => setMode('manual')}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                <Plus className="h-4 w-4 inline mr-2" />
                Create New Template
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Layout className="h-5 w-5 text-orange-500" />
                      <h4 className="font-medium text-gray-900">{template.name}</h4>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTemplate(template.id);
                      }}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Delete template"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-600 mb-3">
                    <p className="text-sm text-gray-700 font-medium">{template.configuration.title}</p>
                    <p className="text-xs text-gray-500">{template.configuration.description}</p>
                    <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Layout:</span>
                      <span className="capitalize">{template.configuration.layout}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Form Fields:</span>
                      <span>{template.configuration.form_fields?.length || 0} fields</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className="flex-1 px-3 py-1.5 bg-theme-blue text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Edit3 className="h-3.5 w-3.5" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleGeneratePopupFromTemplate(template)}
                      className="flex-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Generate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Form Builder - Hidden, only for AI Generate workflow (not shown by default) */}
      {showTemplates === false && (
        <div className="space-y-6">
          {/* Popup Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Popup Name (Internal)
            </label>
            <input
              type="text"
              value={popupName}
              onChange={(e) => setPopupName(e.target.value)}
              placeholder="e.g., 'Summer Discount 2024'"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          {/* Popup Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Popup Title
              </label>
              <input
                type="text"
                value={popupTitle}
                onChange={(e) => setPopupTitle(e.target.value)}
                placeholder="Get 10% Off Your Next Visit!"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Size
              </label>
              <select
                value={size}
                onChange={(e) => setSize(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
                <option value="fullscreen">Full Screen</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={popupDescription}
              onChange={(e) => setPopupDescription(e.target.value)}
              placeholder="Sign up now to receive exclusive offers..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows="3"
            />
          </div>

          {/* Popup Layout Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Popup Layout
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                <input
                  type="radio"
                  name="popupLayout"
                  value="left"
                  checked={popupLayout === 'left'}
                  onChange={(e) => setPopupLayout(e.target.value)}
                  className="mr-3 mt-0.5"
                />
                <div>
                  <div className="font-medium text-sm">Image Left</div>
                  <div className="text-xs text-gray-500">Image on left side</div>
                </div>
              </label>
              <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                <input
                  type="radio"
                  name="popupLayout"
                  value="right"
                  checked={popupLayout === 'right'}
                  onChange={(e) => setPopupLayout(e.target.value)}
                  className="mr-3 mt-0.5"
                />
                <div>
                  <div className="font-medium text-sm">Image Right</div>
                  <div className="text-xs text-gray-500">Image on right side</div>
                </div>
              </label>
              <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                <input
                  type="radio"
                  name="popupLayout"
                  value="background"
                  checked={popupLayout === 'background'}
                  onChange={(e) => setPopupLayout(e.target.value)}
                  className="mr-3 mt-0.5"
                />
                <div>
                  <div className="font-medium text-sm">Background Image</div>
                  <div className="text-xs text-gray-500">Image behind form</div>
                </div>
              </label>
              <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                <input
                  type="radio"
                  name="popupLayout"
                  value="text_only"
                  checked={popupLayout === 'text_only'}
                  onChange={(e) => {
                    setPopupLayout(e.target.value);
                    setPopupImage('');
                    setImageFile(null);
                  }}
                  className="mr-3 mt-0.5"
                />
                <div>
                  <div className="font-medium text-sm">Text Only</div>
                  <div className="text-xs text-gray-500">No image, just text</div>
                </div>
              </label>
            </div>
            
            {/* Image Format Selection - Only show for layouts with images */}
            {['left', 'right', 'background'].includes(popupLayout) && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image Format
                </label>
                <select
                  value={imageFormat}
                  onChange={(e) => setImageFormat(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {popupLayout === 'background' ? (
                    // Background layout formats - show landscape and square
                    Object.keys(popupFormats).length > 0 ? (
                      Object.entries(popupFormats)
                        .filter(([key]) => key === 'landscape' || key === 'square')
                        .map(([key, format]) => (
                          <option key={key} value={key}>
                            {key.charAt(0).toUpperCase() + key.slice(1)}
                          </option>
                        ))
                    ) : (
                      <>
                        <option value="landscape">Landscape</option>
                        <option value="square">Square</option>
                      </>
                    )
                  ) : (
                    // Split layout formats (left/right) - show portrait and square
                    Object.keys(popupFormats).length > 0 ? (
                      Object.entries(popupFormats)
                        .filter(([key]) => key === 'portrait' || key === 'square')
                        .map(([key, format]) => (
                          <option key={key} value={key}>
                            {key.charAt(0).toUpperCase() + key.slice(1)}
                          </option>
                        ))
                    ) : (
                      <>
                        <option value="portrait">Portrait</option>
                        <option value="square">Square</option>
                      </>
                    )
                  )}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {popupLayout === 'background' 
                    ? 'Background images fill the full modal width.'
                    : 'Split layout images fit beside the form (50/50).'}
                </p>
              </div>
            )}

            {/* Image Source Options - Only show if not text_only */}
            {popupLayout !== 'text_only' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Image Source
                </label>
                <div className="flex gap-3">
                  <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border flex-1">
                    <input
                      type="radio"
                      name="imageSource"
                      value="ai_generate"
                      checked={imageSource === 'ai_generate'}
                      onChange={(e) => setImageSource(e.target.value)}
                      className="mr-3 mt-0.5"
                    />
                    <div>
                      <div className="font-medium text-sm">AI Generate</div>
                      <div className="text-xs text-gray-500">Create with AI</div>
                    </div>
                  </label>
                  <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border flex-1">
                    <input
                      type="radio"
                      name="imageSource"
                      value="upload"
                      checked={imageSource === 'upload'}
                      onChange={(e) => setImageSource(e.target.value)}
                      className="mr-3 mt-0.5"
                    />
                    <div>
                      <div className="font-medium text-sm">Upload</div>
                      <div className="text-xs text-gray-500">Use your own</div>
                    </div>
                  </label>
                  <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border flex-1">
                    <input
                      type="radio"
                      name="imageSource"
                      value="s3_browse"
                      checked={imageSource === 's3_browse'}
                      onChange={(e) => {
                        setImageSource(e.target.value);
                        fetchS3Assets();
                        setShowS3Browser(true);
                      }}
                      className="mr-3 mt-0.5"
                    />
                    <div>
                      <div className="font-medium text-sm">Browse</div>
                      <div className="text-xs text-gray-500">Select existing</div>
                    </div>
                  </label>
                </div>

                {/* AI Generate Button and Model Selection - Only show when no image */}
                {imageSource === 'ai_generate' && !popupImage && (
                  <div className="mt-3">
                    {/* Model Selection and Generate Button - Inline */}
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Model Selection Dropdown - Orange */}
                      <select
                        value={selectedImageModel}
                        onChange={(e) => setSelectedImageModel(e.target.value)}
                        className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 transition-colors appearance-none pr-8"
                        style={{ 
                          backgroundColor: '#ea580c',
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                          backgroundPosition: 'right 6px center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '14px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#c2410c';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#ea580c';
                        }}
                      >
                        {availableImageModels.map((model) => (
                          <option key={model.id} value={model.id} style={{ backgroundColor: 'white', color: 'black' }}>
                            {model.name}
                          </option>
                        ))}
                      </select>
                      
                      {/* Generate Button - Green and Compact */}
                      <button
                        onClick={handleAIImageGenerate}
                        disabled={!popupTitle}
                        className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5 font-medium"
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                        {popupImage ? 'Regenerate' : 'Generate'}
                      </button>
                    </div>
                    {!popupTitle && (
                      <p className="text-xs text-gray-500 mt-2">Add a popup title first to generate AI image</p>
                    )}
                  </div>
                )}

                {/* Upload Button */}
                {imageSource === 'upload' && (
                  <div className="mt-3">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e.target.files[0])}
                        className="hidden"
                      />
                      <div className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 inline-flex items-center">
                        <Upload className="h-4 w-4 mr-2" />
                        {imageFile ? 'Change Image' : 'Upload Image'}
                      </div>
                    </label>
                    {imageFile && !popupImage && (
                      <p className="text-sm text-gray-600 mt-2">Selected: {imageFile.name}</p>
                    )}
                  </div>
                )}

                {/* S3 Browse - Show selected image name */}
                {imageSource === 's3_browse' && popupImage && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600">Image selected from library</p>
                  </div>
                )}

                {/* Image Preview and Controls - Styled like Edit Modal */}
                {popupImage && (
                  <div className="mt-3 space-y-3">
                    {/* Image Preview - Larger like Edit Modal */}
                    <div className="space-y-3">
                      <img src={popupImage} alt="Preview" className="w-full max-w-md h-auto object-cover rounded-md border" />
                      
                      {/* Model Selection and Buttons - Below Image */}
                      {imageSource === 'ai_generate' && (
                        <div className="flex flex-wrap items-center gap-2">
                          {/* Model Selection Dropdown - Orange */}
                          <select
                            value={selectedImageModel}
                            onChange={(e) => setSelectedImageModel(e.target.value)}
                            className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 transition-colors appearance-none pr-8"
                            style={{ 
                              backgroundColor: '#ea580c',
                              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                              backgroundPosition: 'right 6px center',
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: '14px'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#c2410c';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#ea580c';
                            }}
                          >
                            {availableImageModels.map((model) => (
                              <option key={model.id} value={model.id} style={{ backgroundColor: 'white', color: 'black' }}>
                                {model.name}
                              </option>
                            ))}
                          </select>
                          
                          {/* Regenerate Button - Green */}
                          <button
                            onClick={handleAIImageGenerate}
                            disabled={!popupTitle}
                            className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5 font-medium"
                          >
                            <Sparkles className="h-3.5 w-3.5" />
                            Regenerate
                          </button>
                          
                          {/* Remove Button */}
                          <button
                            onClick={() => {
                              setPopupImage('');
                              setImageFile(null);
                            }}
                            className="px-3 py-1.5 text-red-500 hover:text-red-700 text-sm border border-red-300 rounded"
                          >
                            Remove Image
                          </button>
                        </div>
                      )}
                      
                      {/* Remove button for non-AI images */}
                      {imageSource !== 'ai_generate' && (
                        <button
                          onClick={() => {
                            setPopupImage('');
                            setImageFile(null);
                          }}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          Remove Image
                        </button>
                      )}
                    </div>
                    
                    {popupLayout === 'background' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Background Image Opacity: {imageOpacity}%
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={imageOpacity}
                          onChange={(e) => setImageOpacity(parseInt(e.target.value))}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Transparent</span>
                          <span>Opaque</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Custom Banner Text Section */}
          {(imageSource === 'ai_generate' || imageSource === 'upload' || imageSource === 's3_browse') && popupImage && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={enableCustomBannerText}
                  onChange={(e) => setEnableCustomBannerText(e.target.checked)}
                  className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3 mt-0.5"
                />
                <span className="block text-sm font-medium text-gray-700">
                  Add Custom Text Banner to Image
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-2 ml-7">
                Add custom text overlay below the image (e.g., business name, phone number)
              </p>
              
              {enableCustomBannerText && (
                <div className="mt-3 ml-7">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Custom Banner Text
                    </label>
                  </div>
                  <input
                    type="text"
                    value={customBannerText}
                    onChange={(e) => setCustomBannerText(e.target.value)}
                    className="w-full px-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                    placeholder="[BUSINESS_NAME] [PHONE_NUMBER]"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Use placeholders like [BUSINESS_NAME], [PHONE_NUMBER], [EMAIL], [WEBSITE]
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Popup Background Styling Section */}
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Popup Background Styling</h3>
            
            {/* Background Color */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={popupBackgroundColor}
                  onChange={(e) => setPopupBackgroundColor(e.target.value)}
                  className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={popupBackgroundColor}
                  onChange={(e) => setPopupBackgroundColor(e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="#ffffff"
                />
              </div>
            </div>
            
            {/* Background Opacity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Opacity: {popupBackgroundOpacity}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={popupBackgroundOpacity}
                onChange={(e) => setPopupBackgroundOpacity(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${popupBackgroundOpacity}%, #e5e7eb ${popupBackgroundOpacity}%, #e5e7eb 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0% (Transparent)</span>
                <span>100% (Solid)</span>
              </div>
            </div>
          </div>

          {/* Form Fields Builder */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Form Fields
              </label>
              <button
                onClick={addFormField}
                className="text-sm text-theme-blue hover:text-blue-700 flex items-center"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Field
              </button>
            </div>
            <div className="space-y-3">
              {formFields.map((field, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <select
                      value={field.field_type}
                      onChange={(e) => updateFormField(index, { field_type: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      {fieldTypes.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={field.label}
                      onChange={(e) => updateFormField(index, { label: e.target.value })}
                      placeholder="Field Label"
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <input
                      type="text"
                      value={field.placeholder}
                      onChange={(e) => updateFormField(index, { placeholder: e.target.value })}
                      placeholder="Placeholder"
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <div className="flex items-center gap-2">
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => updateFormField(index, { required: e.target.checked })}
                          className="mr-1"
                        />
                        Required
                      </label>
                      <button
                        onClick={() => moveFieldUp(index)}
                        disabled={index === 0}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => moveFieldDown(index)}
                        disabled={index === formFields.length - 1}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        ↓
                      </button>
                      <button
                        onClick={() => removeFormField(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Button Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Button Text
              </label>
              <input
                type="text"
                value={buttonText}
                onChange={(e) => setButtonText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Button Color
              </label>
              <input
                type="color"
                value={buttonColor}
                onChange={(e) => setButtonColor(e.target.value)}
                className="w-full h-10 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            {mode === 'ai' && !showTemplates && (
              <button
                onClick={() => setShowTemplates(true)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Back to Templates
              </button>
            )}
            <button
              onClick={() => setShowPreviewModal(true)}
              disabled={!popupTitle}
              className="px-4 py-2 border border-teal-500 text-teal-600 rounded-md hover:bg-teal-50 disabled:opacity-50 flex items-center"
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </button>
            <button
              onClick={handleSavePopup}
              disabled={saving}
              className="px-6 py-2 bg-theme-blue text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Create Template
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Generating Image Modal - Compact */}
      {showGeneratingImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-xs w-full mx-4">
            <div className="flex flex-col items-center">
              {generatingModalStatus === 'generating' && (
                <>
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mb-3"></div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">Generating...</h3>
                  <p className="text-xs text-gray-600 text-center">
                    Creating your image
                  </p>
                </>
              )}
              
              {generatingModalStatus === 'success' && (
                <>
                  <div className="rounded-full h-10 w-10 bg-green-100 flex items-center justify-center mb-3">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">Success!</h3>
                  <p className="text-xs text-gray-600 text-center">
                    {generatingModalMessage}
                  </p>
                </>
              )}
              
              {generatingModalStatus === 'error' && (
                <>
                  <div className="rounded-full h-10 w-10 bg-red-100 flex items-center justify-center mb-3">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">Error</h3>
                  <p className="text-xs text-gray-600 text-center mb-3">
                    {generatingModalMessage}
                  </p>
                  <button
                    onClick={() => {
                      setShowGeneratingImageModal(false);
                      setGeneratingModalStatus('generating');
                      setGeneratingModalMessage('');
                    }}
                    className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Generating Modal */}
      {showGeneratingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating Popup...</h3>
              <p className="text-sm text-gray-600 text-center">
                Creating your popup content with AI
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-lg font-semibold text-gray-900">Popup Preview</h3>
              <button 
                onClick={() => setShowPreviewModal(false)} 
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-8 bg-gray-100">
              {/* Popup Preview */}
              <div 
                className={`rounded-lg shadow-2xl mx-auto relative overflow-hidden ${
                  size === 'small' ? 'max-w-sm' : 
                  size === 'medium' ? 'max-w-md' : 
                  size === 'large' ? 'max-w-2xl' : 
                  'w-full'
                }`}
                style={{
                  backgroundColor: popupBackgroundColor,
                  opacity: popupBackgroundOpacity / 100,
                  ...(popupImage ? {
                    backgroundImage: `url(${popupImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  } : {})
                }}
              >
                {/* Overlay for opacity control */}
                {popupImage && (
                  <div 
                    className="absolute inset-0 bg-white rounded-lg" 
                    style={{ opacity: 1 - (imageOpacity / 100) }}
                  />
                )}
                <div className="p-6 relative z-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">{popupTitle || 'Your Title Here'}</h2>
                  {popupDescription && (
                    <p className="text-gray-600 mb-4">{popupDescription}</p>
                  )}
                  <div className="space-y-3">
                    {formFields.map((field, index) => (
                      <div key={index}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label} {field.required && <span className="text-red-500">*</span>}
                        </label>
                        {field.field_type === 'textarea' ? (
                          <textarea 
                            placeholder={field.placeholder}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                            rows={3}
                            disabled
                          />
                        ) : field.field_type === 'checkbox' ? (
                          <label className="flex items-center">
                            <input type="checkbox" className="mr-2" disabled />
                            <span className="text-sm text-gray-600">{field.placeholder || field.label}</span>
                          </label>
                        ) : (
                          <input 
                            type={field.field_type}
                            placeholder={field.placeholder}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                            disabled
                          />
                        )}
                      </div>
                    ))}
                    <button 
                      style={{ backgroundColor: buttonColor }}
                      className="w-full py-3 text-white rounded-md font-medium"
                      disabled
                    >
                      {buttonText}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* S3 Browser Modal */}
      {showS3Browser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Select Image</h3>
              <button 
                onClick={() => setShowS3Browser(false)} 
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              {s3Assets.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No images found. Upload images in Manage Assets.</p>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                  {s3Assets.map((asset) => (
                    <div
                      key={asset.key}
                      onClick={() => handleS3ImageSelect(asset.url)}
                      className="cursor-pointer border-2 border-gray-200 rounded-lg overflow-hidden hover:border-teal-500 transition-colors"
                    >
                      <img 
                        src={asset.url} 
                        alt={asset.original_filename || 'Asset'} 
                        className="w-full h-32 object-cover"
                      />
                      <div className="p-2 bg-gray-50">
                        <p className="text-xs text-gray-600 truncate">{asset.original_filename || 'Image'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Create Template Modal */}
      {showCreateTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header - Sticky with teal background */}
            <div className="flex-shrink-0 px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <h2 className="text-xl font-semibold text-white">Create New Template</h2>
              <button
                onClick={() => {
                  setShowCreateTemplateModal(false);
                  setTemplateDescription('');
                  setTemplateError('');
                }}
                className="text-white hover:text-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Error Message - Compact */}
              {templateError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-800">{templateError}</p>
                  </div>
                  <button
                    onClick={() => setTemplateError('')}
                    className="text-red-400 hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Describe Your Template
                </label>
                <textarea
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Describe the popup template you want... (e.g., 'A newsletter signup popup with email field, first name, and subscribe button. Left layout with portrait image.')"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                  rows="6"
                  disabled={generatingTemplateStructure}
                />
                <p className="text-xs text-gray-500 mt-2">
                  AI will generate the complete template structure including layout, form fields, button styling, and colors based on your description.
                </p>
              </div>
            </div>

            {/* Footer - Light grey background */}
            <div className="flex-shrink-0 px-6 py-4 flex justify-end gap-3 bg-gray-100 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowCreateTemplateModal(false);
                  setTemplateDescription('');
                  setTemplateError('');
                }}
                disabled={generatingTemplateStructure}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateTemplate}
                disabled={!templateDescription.trim() || generatingTemplateStructure}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
              >
                {generatingTemplateStructure ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Generate Template
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Template Modal */}
      {showEditTemplateModal && editingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full flex flex-col overflow-hidden">
            {/* Header - Teal background */}
            <div className="flex-shrink-0 px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <h2 className="text-xl font-semibold text-white">Edit Template</h2>
              <button
                onClick={() => {
                  setShowEditTemplateModal(false);
                  setEditingTemplate(null);
                  setEditTemplateName('');
                }}
                className="text-white hover:text-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name
                </label>
                <input
                  type="text"
                  value={editTemplateName}
                  onChange={(e) => setEditTemplateName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="e.g., Newsletter Signup"
                />
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Template Details</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <div><span className="font-medium">Title:</span> {editingTemplate.configuration.title}</div>
                  <div><span className="font-medium">Layout:</span> {editingTemplate.configuration.layout}</div>
                  <div><span className="font-medium">Form Fields:</span> {editingTemplate.configuration.form_fields?.length || 0}</div>
                </div>
              </div>
            </div>

            {/* Footer - Light grey background */}
            <div className="flex-shrink-0 px-6 py-4 flex justify-end gap-3 bg-gray-100 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowEditTemplateModal(false);
                  setEditingTemplate(null);
                  setEditTemplateName('');
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateTemplate}
                disabled={!editTemplateName.trim()}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PopupListTab = ({ popups, loading, API_BASE_URL, showMessage, onUpdate }) => {
  const [configModal, setConfigModal] = useState({ show: false, popup: null });
  const [deleteModal, setDeleteModal] = useState({ show: false, popup: null });
  const [editModal, setEditModal] = useState({ show: false, popup: null });
  const [previewModal, setPreviewModal] = useState({ show: false, popup: null });
  const [showGeneratingImageModal, setShowGeneratingImageModal] = useState(false);
  
  // State for image generation modal (status and message)
  const [generatingModalStatus, setGeneratingModalStatus] = useState('generating'); // 'generating', 'success', 'error'
  const [generatingModalMessage, setGeneratingModalMessage] = useState('');

  const [publicPages, setPublicPages] = useState([]);
  const [updating, setUpdating] = useState(false);
  
  // Configuration state
  const [enabled, setEnabled] = useState(false);
  const [triggerType, setTriggerType] = useState('immediate');
  const [triggerValue, setTriggerValue] = useState(5);
  const [displayPages, setDisplayPages] = useState([]);
  const [position, setPosition] = useState('center');
  const [closeButtonBehavior, setCloseButtonBehavior] = useState('always');
  const [closeButtonDelay, setCloseButtonDelay] = useState(0);
  
  // Edit state
  const [editName, setEditName] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editImage, setEditImage] = useState('');
  const [editImageFile, setEditImageFile] = useState(null);
  const [editPopupLayout, setEditPopupLayout] = useState('left'); // 'left', 'right', 'background', 'text_only'
  const [editImageFormat, setEditImageFormat] = useState('portrait'); // 'portrait' or 'square' - format for left/right/background layouts
  const [editImageSource, setEditImageSource] = useState('upload'); // 'ai_generate', 'upload', 's3_browse'
  const [editImageOpacity, setEditImageOpacity] = useState(100);
  const [editFormFields, setEditFormFields] = useState([]);
  const [editButtonText, setEditButtonText] = useState('Submit');
  const [editSize, setEditSize] = useState('medium');
  const [editButtonColor, setEditButtonColor] = useState('var(--theme-primary)');
  
  // Banner text states for edit modal
  const [editEnableCustomBannerText, setEditEnableCustomBannerText] = useState(false);
  const [editCustomBannerText, setEditCustomBannerText] = useState('[BUSINESS_NAME] [PHONE_NUMBER]');
  
  // Popup background styling for edit modal
  const [editPopupBackgroundColor, setEditPopupBackgroundColor] = useState('#ffffff');
  const [editPopupBackgroundOpacity, setEditPopupBackgroundOpacity] = useState(100);
  
  // Image model selection states for edit modal
  const [availableImageModels, setAvailableImageModels] = useState([]);
  const [selectedImageModel, setSelectedImageModel] = useState('');
  const [useOpenAI, setUseOpenAI] = useState(false);
  
  // Popup formats from AI Settings
  const [popupFormats, setPopupFormats] = useState({});
  
  // Local error/success message for edit modal
  const [editModalMessage, setEditModalMessage] = useState({ type: '', text: '' });

  // Auto-adjust image format when EDIT modal layout changes
  useEffect(() => {
    if (editPopupLayout === 'background') {
      // For background layout, default to landscape if portrait is selected
      // But allow square to remain as square
      if (editImageFormat === 'portrait') {
        setEditImageFormat('landscape');
      }
    } else if (editPopupLayout === 'left' || editPopupLayout === 'right') {
      // Default to portrait for left/right layouts
      if (!editImageFormat || editImageFormat === 'landscape') {
        setEditImageFormat('portrait');
      }
    }
  }, [editPopupLayout]);

  useEffect(() => {
    fetchPublicPages();
    fetchImageModels(); // Fetch image models on mount
  }, []);

  useEffect(() => {
    // Refetch pages if they're empty when modal opens
    if (configModal.show && publicPages.length === 0) {
      fetchPublicPages();
    }
  }, [configModal.show]);

  const fetchPublicPages = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/popups/public-pages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setPublicPages(response.data.pages);
      }
    } catch (error) {
      console.error('Error fetching public pages:', error);
      // Set default pages if API fails
      setPublicPages([
        { value: '/', label: 'Home' },
        { value: '/services', label: 'Services' },
        { value: '/about', label: 'About Us' },
        { value: '/our-team', label: 'Our Team' },
        { value: '/gallery', label: 'Gallery' },
        { value: '/reviews', label: 'Reviews' },
        { value: '/our-hours', label: 'Our Hours' },
        { value: '/urgent-care', label: 'Urgent Care' },
        { value: '/contact', label: 'Contact' },
        { value: '/forms-download', label: 'Forms Download' },
        { value: '/reach-us', label: 'Reach Us' },
        { value: '/message-us', label: 'Message Us' },
        { value: '/specialty-services', label: 'Specialty Services' },
        { value: '/our-facility', label: 'Our Facility' },
        { value: '/our-mission', label: 'Our Mission' },
        { value: '/our-values', label: 'Our Values' }
      ]);
    }
  };
  
  const fetchImageModels = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Fetch OpenAI settings to check if override is enabled
      const openaiResponse = await axios.get(`${API_BASE_URL}/api/openai-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const openaiData = openaiResponse.data;
      const isOpenAIEnabled = openaiData?.settings?.override_llm === true;
      setUseOpenAI(isOpenAIEnabled);
      
      if (isOpenAIEnabled) {
        // Use OpenAI image models - convert from {value, label} to {id, name}
        const imageModelsData = (openaiData.image_models || []).map(model => ({
          id: model.value || model.id,
          name: model.label || model.name
        }));
        setAvailableImageModels(imageModelsData);
        
        // Set default from settings
        const defaultImageModel = openaiData.settings.image_generation_model || 'dall-e-3';
        setSelectedImageModel(defaultImageModel);
      } else {
        // Use OpenRouter image models
        const openRouterResponse = await axios.get(`${API_BASE_URL}/api/openrouter-models`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (openRouterResponse.data.available_models) {
          const imageModels = Object.entries(openRouterResponse.data.available_models)
            .filter(([_, modelInfo]) => modelInfo.category === 'image_generation')
            .map(([modelId, modelInfo]) => ({
              id: modelId,
              name: modelInfo.name
            }));
          
          setAvailableImageModels(imageModels);
          
          if (imageModels.length > 0) {
            setSelectedImageModel(imageModels[0].id);
          }
        }
      }
      
      // Fetch AI Settings to get popup formats
      try {
        const aiSettingsResponse = await axios.get(`${API_BASE_URL}/api/ai-settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (aiSettingsResponse.data?.social_media_image_settings?.popup?.formats) {
          setPopupFormats(aiSettingsResponse.data.social_media_image_settings.popup.formats);
          console.log('✅ Loaded popup formats:', aiSettingsResponse.data.social_media_image_settings.popup.formats);
        }
      } catch (error) {
        console.error('Error fetching popup formats:', error);
      }
    } catch (error) {
      console.error('Error fetching image models:', error);
      // Fallback defaults including Nano Banana
      setAvailableImageModels([
        { id: 'dall-e-3', name: 'DALL-E 3' },
        { id: 'gpt-image-1', name: 'GPT Image 1' },
        { id: 'google/gemini-2.5-flash-image-preview', name: 'Nano Banana (Google Gemini)' }
      ]);
      setSelectedImageModel('dall-e-3');
    }
  };

  const openConfigModal = (popup) => {
    setConfigModal({ show: true, popup });
    setEnabled(popup.enabled || false);
    setTriggerType(popup.trigger?.type || 'immediate');
    setTriggerValue(popup.trigger?.value || 5);
    setDisplayPages(popup.display_on_pages || []);
    setPosition(popup.configuration?.position || 'center');
    setCloseButtonBehavior(popup.configuration?.close_button_behavior || 'always');
    setCloseButtonDelay(popup.configuration?.close_button_delay || 0);
  };

  const closeConfigModal = () => {
    setConfigModal({ show: false, popup: null });
  };

  const openEditModal = (popup) => {
    console.log('Opening edit modal with popup:', popup);
    console.log('Layout from configuration:', popup.configuration?.layout);
    
    setEditModal({ show: true, popup });
    setEditName(popup.name || '');
    setEditTitle(popup.configuration?.title || '');
    setEditDescription(popup.configuration?.description || '');
    setEditImage(popup.configuration?.image_url || '');
    setEditImageFile(null);
    
    const layout = popup.configuration?.layout || 'left';
    console.log('Setting editPopupLayout to:', layout);
    setEditPopupLayout(layout);
    
    // Restore the image_source from saved configuration, default to 'ai_generate' if not saved
    setEditImageSource(popup.configuration?.image_source || 'ai_generate');
    setEditImageOpacity(popup.configuration?.image_opacity || 100);
    setEditFormFields(popup.configuration?.form_fields || []);
    setEditButtonText(popup.configuration?.button_text || 'Submit');
    setEditSize(popup.configuration?.size || 'medium');
    setEditButtonColor(popup.configuration?.button_color || 'var(--theme-primary)');
    
    // Load image format (portrait or square)
    setEditImageFormat(popup.configuration?.image_format || 'portrait');
    
    // Load banner text settings
    setEditEnableCustomBannerText(popup.configuration?.enable_custom_banner_text || false);
    setEditCustomBannerText(popup.configuration?.custom_banner_text || '[BUSINESS_NAME] [PHONE_NUMBER]');
    
    // Load popup background styling
    setEditPopupBackgroundColor(popup.configuration?.background_color || '#ffffff');
    setEditPopupBackgroundOpacity(popup.configuration?.background_opacity || 100);
  };

  const closeEditModal = () => {
    setEditModal({ show: false, popup: null });
    setEditModalMessage({ type: '', text: '' }); // Clear message when closing modal
  };

  const handleEditAIImageGenerate = async () => {
    if (!editTitle && !editDescription) {
      setGeneratingModalStatus('error');
      setGeneratingModalMessage('Please provide popup title or description first');
      setShowGeneratingImageModal(true);
      return;
    }
    
    try {
      setShowGeneratingImageModal(true);
      setGeneratingModalStatus('generating');
      setGeneratingModalMessage('');
      
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Fetch AI settings to get image model
      let imageModel = 'google/gemini-2.5-flash-image-preview'; // default to Nano Banana
      
      try {
        const settingsResponse = await axios.get(`${API_BASE_URL}/api/ai-settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (settingsResponse.data) {
          // Use the selected image model from component state (user selection or default from settings)
          imageModel = selectedImageModel || settingsResponse.data.image_model || 'google/gemini-2.5-flash-image-preview';
        }
      } catch (error) {
        console.error('Error fetching AI settings:', error);
      }
      
      const imagePrompt = `Create a professional marketing background image for a popup promotion: ${editTitle}. ${editDescription}. Modern, clean, eye-catching design. NO text in the image. Background style suitable for overlaying text.`;
      
      console.log('🎨 [EDIT MODE] Image Generation Debug:');
      console.log('  Layout:', editPopupLayout);
      console.log('  Selected Format:', editImageFormat);
      console.log('  Image Model:', imageModel);
      console.log('  Enable Custom Banner:', editEnableCustomBannerText);
      console.log('  Custom Banner Text:', editEnableCustomBannerText ? editCustomBannerText : 'N/A');
      
      const response = await axios.post(
        `${API_BASE_URL}/api/ai/generate-image`,
        {
          prompt: imagePrompt,
          model: imageModel,
          platform: 'popup',  // Send platform to get AI settings
          format: editImageFormat,  // Send the actual selected format: 'portrait', 'square', or 'landscape'
          enable_custom_image_text: editEnableCustomBannerText,  // Pass banner text flag
          custom_text: editEnableCustomBannerText ? editCustomBannerText : null  // Pass banner text if enabled
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.image_url) {
        setEditImage(response.data.image_url);
        setEditImageFile(null);
        
        // Show success in modal
        setGeneratingModalStatus('success');
        setGeneratingModalMessage('AI image generated successfully!');
        
        // Auto-close after 2 seconds
        setTimeout(() => {
          setShowGeneratingImageModal(false);
          setGeneratingModalStatus('generating');
          setGeneratingModalMessage('');
          setEditModalMessage({ type: '', text: '' }); // Clear edit modal message too
        }, 2000);
      } else {
        setGeneratingModalStatus('error');
        setGeneratingModalMessage('Failed to generate AI image');
      }
    } catch (error) {
      console.error('Error generating AI image:', error);
      
      let errorMessage = 'Failed to generate AI image - please try again';
      if (error.code === 'ERR_NETWORK' || error.message.includes('network')) {
        errorMessage = 'Network error - please check your connection and try again';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setGeneratingModalStatus('error');
      setGeneratingModalMessage(errorMessage);
    }
  };

  const fieldTypes = [
    { value: 'text', label: 'Text Field' },
    { value: 'email', label: 'Email Field' },
    { value: 'phone', label: 'Phone Field' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'checkbox', label: 'Checkbox' }
  ];

  const addEditFormField = () => {
    setEditFormFields([...editFormFields, {
      field_type: 'text',
      label: 'New Field',
      placeholder: '',
      required: true,
      order: editFormFields.length
    }]);
  };

  const updateEditFormField = (index, updates) => {
    const updated = [...editFormFields];
    updated[index] = { ...updated[index], ...updates };
    setEditFormFields(updated);
  };

  const removeEditFormField = (index) => {
    setEditFormFields(editFormFields.filter((_, i) => i !== index));
  };

  const moveEditFieldUp = (index) => {
    if (index === 0) return;
    const updated = [...editFormFields];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    updated.forEach((field, i) => field.order = i);
    setEditFormFields(updated);
  };

  const moveEditFieldDown = (index) => {
    if (index === editFormFields.length - 1) return;
    const updated = [...editFormFields];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    updated.forEach((field, i) => field.order = i);
    setEditFormFields(updated);
  };

  const handleEditImageUpload = async (file) => {
    if (!file) return;
    setEditImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadEditImageToS3 = async () => {
    if (!editImageFile) return editImage;
    
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', editImageFile);
      formData.append('folder', 'landing-page-popups');

      const response = await axios.post(
        `${API_BASE_URL}/api/upload`,
        formData,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      if (response.data.url) {
        return response.data.url;
      }
      return editImage;
    } catch (error) {
      console.error('Error uploading image:', error);
      return editImage;
    }
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) {
      showMessage('error', 'Please provide a popup name');
      return;
    }
    if (!editTitle.trim()) {
      showMessage('error', 'Please provide a popup title');
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Upload image if changed
      const imageUrl = await uploadEditImageToS3();
      
      const updateData = {
        name: editName,
        configuration: {
          ...editModal.popup.configuration,
          title: editTitle,
          description: editDescription,
          image_url: imageUrl,
          image_opacity: editImageOpacity,
          layout: editPopupLayout,
          image_format: editImageFormat, // Save image format (portrait or square)
          image_source: editImageSource,
          form_fields: editFormFields,
          button_text: editButtonText,
          size: editSize,
          button_color: editButtonColor,
          background_color: editPopupBackgroundColor,
          background_opacity: editPopupBackgroundOpacity,
          enable_custom_banner_text: editEnableCustomBannerText,
          custom_banner_text: editCustomBannerText
        }
      };
      
      console.log('Saving popup with layout:', editPopupLayout);
      console.log('Full update data:', updateData);
      
      await axios.put(
        `${API_BASE_URL}/api/popups/${editModal.popup.id}`,
        updateData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      showMessage('success', 'Popup updated successfully');
      closeEditModal();
      onUpdate();
    } catch (error) {
      console.error('Error updating popup:', error);
      showMessage('error', 'Failed to update popup');
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleEnabled = async (popup) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/api/popups/${popup.id}`,
        { enabled: !popup.enabled },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      showMessage('success', `Popup ${!popup.enabled ? 'enabled' : 'disabled'} successfully`);
      onUpdate();
    } catch (error) {
      console.error('Error toggling popup:', error);
      showMessage('error', 'Failed to update popup');
    }
  };

  const handleSaveConfig = async () => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Update trigger
      await axios.put(
        `${API_BASE_URL}/api/popups/${configModal.popup.id}`,
        {
          enabled,
          trigger: { type: triggerType, value: triggerValue },
          display_on_pages: displayPages,
          configuration: {
            ...configModal.popup.configuration,
            position,
            close_button_behavior: closeButtonBehavior,
            close_button_delay: closeButtonDelay
          }
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      showMessage('success', 'Configuration saved successfully');
      closeConfigModal();
      onUpdate();
    } catch (error) {
      console.error('Error saving config:', error);
      showMessage('error', 'Failed to save configuration');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/api/popups/${deleteModal.popup.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      showMessage('success', 'Popup deleted successfully');
      setDeleteModal({ show: false, popup: null });
      onUpdate();
    } catch (error) {
      console.error('Error deleting popup:', error);
      showMessage('error', 'Failed to delete popup');
    }
  };

  const handlePageToggle = (pageValue) => {
    if (displayPages.includes(pageValue)) {
      setDisplayPages(displayPages.filter(p => p !== pageValue));
    } else {
      setDisplayPages([...displayPages, pageValue]);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-blue mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading popups...</p>
      </div>
    );
  }

  if (popups.length === 0) {
    return (
      <div className="text-center py-12">
        <Settings className="h-16 w-16 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Popups Yet</h3>
        <p className="text-sm text-gray-600">Create your first popup in the Generate tab</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {popups.map((popup) => (
          <div key={popup.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h3 className="text-base font-medium text-gray-900">{popup.name}</h3>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {popup.configuration.title}
                </p>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                  <span>Created: {new Date(popup.created_at).toLocaleDateString()}</span>
                  <span>•</span>
                  <span>{popup.submission_count || 0} submissions</span>
                  {popup.display_on_pages && popup.display_on_pages.length > 0 && (
                    <>
                      <span>•</span>
                      <span>{popup.display_on_pages.length} page(s)</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleToggleEnabled(popup)}
                  className={`p-2 rounded-lg transition-colors ${
                    popup.enabled
                      ? 'text-green-600 hover:bg-green-50'
                      : 'text-gray-400 hover:bg-gray-100'
                  }`}
                  title={popup.enabled ? 'Disable' : 'Enable'}
                >
                  {popup.enabled ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                </button>
                <button
                  onClick={() => setPreviewModal({ show: true, popup })}
                  className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  title="Preview"
                >
                  <Eye className="h-5 w-5" />
                </button>
                <button
                  onClick={() => openEditModal(popup)}
                  className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                  title="Edit Content"
                >
                  <Edit3 className="h-5 w-5" />
                </button>
                <button
                  onClick={() => openConfigModal(popup)}
                  className="p-2 text-theme-blue hover:bg-blue-50 rounded-lg transition-colors"
                  title="Configure"
                >
                  <Settings className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setDeleteModal({ show: true, popup })}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Configuration Modal */}
      {configModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Sticky Header */}
            <div className="sticky top-0 px-6 py-4 flex items-center justify-between rounded-t-xl z-10" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <h3 className="text-lg font-semibold text-white">Configure Popup</h3>
              <button onClick={closeConfigModal} className="text-white hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Scrollable Content Area */}
            <div className="px-6 pt-0 pb-6 space-y-6 overflow-y-auto flex-1" style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#cbd5e0 #f7fafc'
            }}>
              <style>{`
                .overflow-y-auto::-webkit-scrollbar {
                  width: 6px;
                }
                .overflow-y-auto::-webkit-scrollbar-track {
                  background: #f7fafc;
                }
                .overflow-y-auto::-webkit-scrollbar-thumb {
                  background: #cbd5e0;
                  border-radius: 3px;
                }
                .overflow-y-auto::-webkit-scrollbar-thumb:hover {
                  background: #a0aec0;
                }
              `}</style>
              {/* Enable/Disable */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Enable this popup</span>
                </label>
              </div>

              {/* Display Trigger */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Trigger
                </label>
                <select
                  value={triggerType}
                  onChange={(e) => setTriggerType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2 text-sm"
                >
                  <option value="immediate">Immediately on page load</option>
                  <option value="timer">After X seconds on page</option>
                  <option value="inactivity">After X seconds of inactivity</option>
                  <option value="scroll">After scrolling X% of page</option>
                  <option value="exit_intent">When user is about to leave</option>
                </select>
                
                {(triggerType === 'timer' || triggerType === 'inactivity') && (
                  <div className="mt-2">
                    <label className="block text-sm text-gray-600 mb-1">Seconds</label>
                    <input
                      type="number"
                      min="1"
                      value={triggerValue}
                      onChange={(e) => setTriggerValue(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                )}
                
                {triggerType === 'scroll' && (
                  <div className="mt-2">
                    <label className="block text-sm text-gray-600 mb-1">Scroll Percentage</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={triggerValue}
                      onChange={(e) => setTriggerValue(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                )}
              </div>

              {/* Display on Pages */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display on Pages (leave empty for all pages)
                </label>
                <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                  {publicPages.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">Loading pages...</p>
                  ) : (
                    publicPages.map((page) => (
                      <label key={page.value} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={displayPages.includes(page.value)}
                          onChange={() => handlePageToggle(page.value)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{page.label}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Position */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Position
                </label>
                <select
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="center">Center</option>
                  <option value="bottom-right">Bottom Right</option>
                  <option value="top-banner">Top Banner</option>
                </select>
              </div>

              {/* Close Button */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Close Button
                </label>
                <select
                  value={closeButtonBehavior}
                  onChange={(e) => setCloseButtonBehavior(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2 text-sm"
                >
                  <option value="always">Always visible</option>
                  <option value="after_delay">Show after delay</option>
                  <option value="none">No close button</option>
                </select>
                
                {closeButtonBehavior === 'after_delay' && (
                  <div className="mt-2">
                    <label className="block text-sm text-gray-600 mb-1">Delay (seconds)</label>
                    <input
                      type="number"
                      min="1"
                      value={closeButtonDelay}
                      onChange={(e) => setCloseButtonDelay(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Sticky Footer */}
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200 rounded-b-xl z-10">
              <button
                onClick={closeConfigModal}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConfig}
                disabled={updating}
                className="px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600 disabled:opacity-50 flex items-center gap-2"
              >
                {updating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Configuration
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Popup Modal */}
      {editModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Sticky Header */}
            <div className="sticky top-0 px-6 py-4 flex items-center justify-between rounded-t-xl z-10" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <h3 className="text-lg font-semibold text-white">Edit Popup Content</h3>
              <button onClick={closeEditModal} className="text-white hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Message Banner - Inside Modal */}
            {editModalMessage.text && (
              <div className={`mx-6 mt-4 px-4 py-3 rounded-lg flex items-center ${
                editModalMessage.type === 'success' 
                  ? 'bg-green-50 text-green-800 border border-green-200' 
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {editModalMessage.type === 'success' ? (
                  <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                )}
                <span className="text-sm">{editModalMessage.text}</span>
              </div>
            )}

            {/* Scrollable Content Area */}
            <div className="px-6 pt-0 pb-6 space-y-6 overflow-y-auto flex-1" style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#cbd5e0 #f7fafc'
            }}>
              <style>{`
                .overflow-y-auto::-webkit-scrollbar {
                  width: 6px;
                }
                .overflow-y-auto::-webkit-scrollbar-track {
                  background: #f7fafc;
                }
                .overflow-y-auto::-webkit-scrollbar-thumb {
                  background: #cbd5e0;
                  border-radius: 3px;
                }
                .overflow-y-auto::-webkit-scrollbar-thumb:hover {
                  background: #a0aec0;
                }
              `}</style>
              {/* Popup Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Popup Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder:text-sm"
                  placeholder="e.g., Holiday Special Offer"
                />
              </div>

              {/* Popup Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Popup Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder:text-sm"
                  placeholder="Catchy headline"
                />
              </div>

              {/* Popup Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder:text-sm"
                  placeholder="Brief description"
                />
              </div>

              {/* Popup Layout Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Popup Layout
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                    <input
                      type="radio"
                      name="editPopupLayout"
                      value="left"
                      checked={editPopupLayout === 'left'}
                      onChange={(e) => setEditPopupLayout(e.target.value)}
                      className="mr-3 mt-0.5"
                    />
                    <div>
                      <div className="font-medium text-sm">Image Left</div>
                      <div className="text-xs text-gray-500">Image on left side</div>
                    </div>
                  </label>
                  <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                    <input
                      type="radio"
                      name="editPopupLayout"
                      value="right"
                      checked={editPopupLayout === 'right'}
                      onChange={(e) => setEditPopupLayout(e.target.value)}
                      className="mr-3 mt-0.5"
                    />
                    <div>
                      <div className="font-medium text-sm">Image Right</div>
                      <div className="text-xs text-gray-500">Image on right side</div>
                    </div>
                  </label>
                  <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                    <input
                      type="radio"
                      name="editPopupLayout"
                      value="background"
                      checked={editPopupLayout === 'background'}
                      onChange={(e) => setEditPopupLayout(e.target.value)}
                      className="mr-3 mt-0.5"
                    />
                    <div>
                      <div className="font-medium text-sm">Background Image</div>
                      <div className="text-xs text-gray-500">Image behind form</div>
                    </div>
                  </label>
                  <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                    <input
                      type="radio"
                      name="editPopupLayout"
                      value="text_only"
                      checked={editPopupLayout === 'text_only'}
                      onChange={(e) => {
                        setEditPopupLayout(e.target.value);
                        setEditImage('');
                        setEditImageFile(null);
                      }}
                      className="mr-3 mt-0.5"
                    />
                    <div>
                      <div className="font-medium text-sm">Text Only</div>
                      <div className="text-xs text-gray-500">No image, just text</div>
                    </div>
                  </label>
                </div>
                
                {/* Image Format Selection - Only show for layouts with images */}
                {['left', 'right', 'background'].includes(editPopupLayout) && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image Format
                    </label>
                    <select
                      value={editImageFormat}
                      onChange={(e) => setEditImageFormat(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {editPopupLayout === 'background' ? (
                        // Background layout formats - show landscape and square
                        Object.keys(popupFormats).length > 0 ? (
                          Object.entries(popupFormats)
                            .filter(([key]) => key === 'landscape' || key === 'square')
                            .map(([key, format]) => (
                              <option key={key} value={key}>
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                              </option>
                            ))
                        ) : (
                          <>
                            <option value="landscape">Landscape</option>
                            <option value="square">Square</option>
                          </>
                        )
                      ) : (
                        // Split layout formats (left/right) - show portrait and square
                        Object.keys(popupFormats).length > 0 ? (
                          Object.entries(popupFormats)
                            .filter(([key]) => key === 'portrait' || key === 'square')
                            .map(([key, format]) => (
                              <option key={key} value={key}>
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                              </option>
                            ))
                        ) : (
                          <>
                            <option value="portrait">Portrait</option>
                            <option value="square">Square</option>
                          </>
                        )
                      )}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {editPopupLayout === 'background' 
                        ? 'Background images fill the full modal width.'
                        : 'Split layout images fit beside the form (50/50).'}
                    </p>
                  </div>
                )}

                {/* Image Source Options - Only show if not text_only */}
                {editPopupLayout !== 'text_only' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Image Source
                    </label>
                    <div className="flex gap-3">
                      <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border flex-1">
                        <input
                          type="radio"
                          name="editImageSource"
                          value="ai_generate"
                          checked={editImageSource === 'ai_generate'}
                          onChange={(e) => setEditImageSource(e.target.value)}
                          className="mr-3 mt-0.5"
                        />
                        <div>
                          <div className="font-medium text-sm">AI Generate</div>
                          <div className="text-xs text-gray-500">Create with AI</div>
                        </div>
                      </label>
                      <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border flex-1">
                        <input
                          type="radio"
                          name="editImageSource"
                          value="upload"
                          checked={editImageSource === 'upload'}
                          onChange={(e) => setEditImageSource(e.target.value)}
                          className="mr-3 mt-0.5"
                        />
                        <div>
                          <div className="font-medium text-sm">Upload</div>
                          <div className="text-xs text-gray-500">Use your own</div>
                        </div>
                      </label>
                      <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border flex-1">
                        <input
                          type="radio"
                          name="editImageSource"
                          value="s3_browse"
                          checked={editImageSource === 's3_browse'}
                          onChange={(e) => {
                            setEditImageSource(e.target.value);
                            fetchS3Assets();
                            setShowS3Browser(true);
                          }}
                          className="mr-3 mt-0.5"
                        />
                        <div>
                          <div className="font-medium text-sm">Browse</div>
                          <div className="text-xs text-gray-500">Select existing</div>
                        </div>
                      </label>
                    </div>

                    {/* Upload Button */}
                    {editImageSource === 'upload' && (
                      <div className="mt-3">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleEditImageUpload(e.target.files[0])}
                            className="hidden"
                          />
                          <div className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 inline-flex items-center">
                            <Upload className="h-4 w-4 mr-2" />
                            {editImageFile ? 'Change Image' : 'Upload Image'}
                          </div>
                        </label>
                        {editImageFile && !editImage && (
                          <p className="text-sm text-gray-600 mt-2">Selected: {editImageFile.name}</p>
                        )}
                      </div>
                    )}

                    {/* S3 Browse - Show selected image name */}
                    {editImageSource === 's3_browse' && editImage && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-600">Image selected from library</p>
                      </div>
                    )}

                    {/* Image Preview and Opacity Control */}
                    {editImage && (
                      <div className="mt-3 space-y-3">
                        {/* Image Preview */}
                        <div className="space-y-3">
                          <img src={editImage} alt="Preview" className="w-full max-w-md h-auto object-cover rounded-md border" />
                          
                          {/* Model Selection and Buttons - Below Image */}
                          {editImageSource === 'ai_generate' && (
                            <div className="flex flex-wrap items-center gap-2">
                              {/* Model Selection Dropdown - Orange */}
                              <select
                                value={selectedImageModel}
                                onChange={(e) => setSelectedImageModel(e.target.value)}
                                className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 transition-colors appearance-none pr-8"
                                style={{ 
                                  backgroundColor: '#ea580c',
                                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                                  backgroundPosition: 'right 6px center',
                                  backgroundRepeat: 'no-repeat',
                                  backgroundSize: '14px'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#c2410c';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = '#ea580c';
                                }}
                              >
                                {availableImageModels.map((model) => (
                                  <option key={model.id} value={model.id} style={{ backgroundColor: 'white', color: 'black' }}>
                                    {model.name}
                                  </option>
                                ))}
                              </select>
                              
                              {/* Regenerate Button - Green */}
                              <button
                                onClick={handleEditAIImageGenerate}
                                disabled={!editTitle}
                                className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5 font-medium"
                              >
                                <Sparkles className="h-3.5 w-3.5" />
                                Regenerate
                              </button>
                              
                              {/* Remove Button */}
                              <button
                                onClick={() => {
                                  setEditImage('');
                                  setEditImageFile(null);
                                }}
                                className="px-3 py-1.5 text-red-500 hover:text-red-700 text-sm border border-red-300 rounded"
                              >
                                Remove Image
                              </button>
                            </div>
                          )}
                          
                          {/* Remove button for non-AI images */}
                          {editImageSource !== 'ai_generate' && (
                            <button
                              onClick={() => {
                                setEditImage('');
                                setEditImageFile(null);
                              }}
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              Remove Image
                            </button>
                          )}
                        </div>
                        
                        {/* Opacity Control */}
                        {editPopupLayout === 'background' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Background Image Opacity: {editImageOpacity}%
                            </label>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={editImageOpacity}
                              onChange={(e) => setEditImageOpacity(parseInt(e.target.value))}
                              className="w-full"
                            />
                            <div className="flex justify-between text-xs text-gray-500">
                              <span>Transparent</span>
                              <span>Opaque</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Generate AI Image Button and Model Selection - Only show when no image and AI Generate selected */}
                    {editImageSource === 'ai_generate' && !editImage && (
                      <div className="mt-3">
                        {/* Model Selection and Generate Button - Inline */}
                        <div className="flex flex-wrap items-center gap-2">
                          {/* Model Selection Dropdown - Orange */}
                          <select
                            value={selectedImageModel}
                            onChange={(e) => setSelectedImageModel(e.target.value)}
                            className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 transition-colors appearance-none pr-8"
                            style={{ 
                              backgroundColor: '#ea580c',
                              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                              backgroundPosition: 'right 6px center',
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: '14px'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#c2410c';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#ea580c';
                            }}
                          >
                            {availableImageModels.map((model) => (
                              <option key={model.id} value={model.id} style={{ backgroundColor: 'white', color: 'black' }}>
                                {model.name}
                              </option>
                            ))}
                          </select>
                          
                          {/* Generate Button - Green and Compact */}
                          <button
                            onClick={handleEditAIImageGenerate}
                            disabled={!editTitle}
                            className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5 font-medium"
                          >
                            <Sparkles className="h-3.5 w-3.5" />
                            Generate
                          </button>
                        </div>
                        {!editTitle && (
                          <p className="text-xs text-gray-500 mt-2">Add a popup title first to generate AI image</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Custom Banner Text Section */}
              {(editImageSource === 'ai_generate' || editImageSource === 'upload' || editImageSource === 's3_browse') && editImage && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={editEnableCustomBannerText}
                      onChange={(e) => setEditEnableCustomBannerText(e.target.checked)}
                      className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3 mt-0.5"
                    />
                    <span className="block text-sm font-medium text-gray-700">
                      Add Custom Text Banner to Image
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-2 ml-7">
                    Add custom text overlay below the image (e.g., business name, phone number)
                  </p>
                  
                  {editEnableCustomBannerText && (
                    <div className="mt-3 ml-7">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Custom Banner Text
                        </label>
                      </div>
                      <input
                        type="text"
                        value={editCustomBannerText}
                        onChange={(e) => setEditCustomBannerText(e.target.value)}
                        className="w-full px-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                        placeholder="[BUSINESS_NAME] [PHONE_NUMBER]"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Use placeholders like [BUSINESS_NAME], [PHONE_NUMBER], [EMAIL], [WEBSITE]
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Popup Background Styling Section */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Popup Background Styling</h3>
                
                {/* Background Color */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={editPopupBackgroundColor}
                      onChange={(e) => setEditPopupBackgroundColor(e.target.value)}
                      className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={editPopupBackgroundColor}
                      onChange={(e) => setEditPopupBackgroundColor(e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="#ffffff"
                    />
                  </div>
                </div>
                
                {/* Background Opacity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Opacity: {editPopupBackgroundOpacity}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={editPopupBackgroundOpacity}
                    onChange={(e) => setEditPopupBackgroundOpacity(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${editPopupBackgroundOpacity}%, #e5e7eb ${editPopupBackgroundOpacity}%, #e5e7eb 100%)`
                    }}
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0% (Transparent)</span>
                    <span>100% (Solid)</span>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Form Fields
                  </label>
                  <button
                    onClick={addEditFormField}
                    className="px-3 py-1.5 bg-teal-500 text-white rounded-md hover:bg-teal-600 text-sm flex items-center gap-1"
                  >
                    <Plus className="h-4 w-4" />
                    Add Field
                  </button>
                </div>

                <div className="space-y-3">
                  {editFormFields.map((field, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Field Type</label>
                          <select
                            value={field.field_type}
                            onChange={(e) => updateEditFormField(index, { field_type: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          >
                            {fieldTypes.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
                          <input
                            type="text"
                            value={field.label}
                            onChange={(e) => updateEditFormField(index, { label: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Placeholder</label>
                          <input
                            type="text"
                            value={field.placeholder || ''}
                            onChange={(e) => updateEditFormField(index, { placeholder: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <label className="flex items-center gap-2 text-xs">
                            <input
                              type="checkbox"
                              checked={field.required}
                              onChange={(e) => updateEditFormField(index, { required: e.target.checked })}
                              className="rounded border-gray-300"
                            />
                            Required
                          </label>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => moveEditFieldUp(index)}
                              disabled={index === 0}
                              className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30"
                            >
                              ↑
                            </button>
                            <button
                              onClick={() => moveEditFieldDown(index)}
                              disabled={index === editFormFields.length - 1}
                              className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30"
                            >
                              ↓
                            </button>
                            <button
                              onClick={() => removeEditFormField(index)}
                              className="p-1 text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Button Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Button Text
                  </label>
                  <input
                    type="text"
                    value={editButtonText}
                    onChange={(e) => setEditButtonText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder:text-sm"
                    placeholder="e.g., Subscribe Now"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Button Color
                  </label>
                  <input
                    type="color"
                    value={editButtonColor}
                    onChange={(e) => setEditButtonColor(e.target.value)}
                    className="w-full h-10 px-1 py-1 border border-gray-300 rounded-md cursor-pointer"
                  />
                </div>
              </div>

              {/* Size Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Popup Size
                </label>
                <div className="flex gap-3">
                  {['small', 'medium', 'large'].map(sizeOption => (
                    <label key={sizeOption} className="flex items-center">
                      <input
                        type="radio"
                        value={sizeOption}
                        checked={editSize === sizeOption}
                        onChange={(e) => setEditSize(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm capitalize">{sizeOption}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Action Buttons - Sticky Footer */}
            </div>
            
            <div className="sticky bottom-0 bg-gray-50 flex justify-end gap-3 px-6 py-4 border-t border-gray-200 rounded-b-xl z-10">
              <button
                onClick={closeEditModal}
                disabled={updating}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={updating}
                className="px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600 disabled:opacity-50 flex items-center gap-2"
              >
                {updating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Popup?</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will permanently delete "{deleteModal.popup?.name}" and all its submissions. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal({ show: false, popup: null })}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewModal.show && previewModal.popup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-lg font-semibold text-gray-900">Popup Preview - {previewModal.popup.name}</h3>
              <button 
                onClick={() => setPreviewModal({ show: false, popup: null })} 
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-8 bg-gray-100">
              {/* Popup Preview with Layout Support */}
              {(() => {
                // Determine layout - default to 'left' if image exists but no layout specified
                const layout = previewModal.popup.configuration.layout || 
                  (previewModal.popup.configuration.image_url ? 'left' : 'text_only');
                
                // Detect image format and adjust widths accordingly
                const imageFormat = previewModal.popup.configuration.image_format || 'portrait';
                const isSquareImage = imageFormat === 'square';
                
                // Use larger widths for left/right layouts to accommodate image
                // Square images need even more width to display fully
                const sizeClass = (layout === 'left' || layout === 'right') ? (
                  previewModal.popup.configuration.size === 'small' ? (isSquareImage ? 'max-w-3xl' : 'max-w-2xl') : 
                  previewModal.popup.configuration.size === 'medium' ? (isSquareImage ? 'max-w-4xl' : 'max-w-3xl') : 
                  previewModal.popup.configuration.size === 'large' ? (isSquareImage ? 'max-w-6xl' : 'max-w-5xl') : 
                  'max-w-6xl'
                ) : (
                  previewModal.popup.configuration.size === 'small' ? 'max-w-sm' : 
                  previewModal.popup.configuration.size === 'medium' ? 'max-w-md' : 
                  previewModal.popup.configuration.size === 'large' ? 'max-w-2xl' : 
                  'max-w-3xl'
                );
                
                return (
                  <div 
                    className={`rounded-lg shadow-2xl mx-auto relative overflow-hidden ${sizeClass}`}
                    style={{
                      backgroundColor: previewModal.popup.configuration.background_color || '#ffffff',
                      opacity: (previewModal.popup.configuration.background_opacity || 100) / 100
                    }}
                  >
                {/* Background Layout */}
                {layout === 'background' && previewModal.popup.configuration.image_url && (
                  <>
                    <div 
                      className="absolute inset-0"
                      style={{
                        backgroundImage: `url(${previewModal.popup.configuration.image_url})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        opacity: (previewModal.popup.configuration.image_opacity || 100) / 100
                      }}
                    />
                    <div className="p-6 relative z-10">
                      <h2 className="text-lg font-bold text-gray-900 mb-3">{previewModal.popup.configuration.title}</h2>
                      {previewModal.popup.configuration.description && (
                        <p className="text-sm text-gray-600 mb-4">{previewModal.popup.configuration.description}</p>
                      )}
                      <div className="space-y-3">
                        {previewModal.popup.configuration.form_fields?.map((field, index) => (
                          <div key={index}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {field.label} {field.required && <span className="text-red-500">*</span>}
                            </label>
                            {field.field_type === 'textarea' ? (
                              <textarea 
                                placeholder={field.placeholder}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                                rows={3}
                                disabled
                              />
                            ) : field.field_type === 'checkbox' ? (
                              <label className="flex items-center">
                                <input type="checkbox" className="mr-2" disabled />
                                <span className="text-sm text-gray-600">{field.placeholder || field.label}</span>
                              </label>
                            ) : (
                              <input 
                                type={field.field_type}
                                placeholder={field.placeholder}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                                disabled
                              />
                            )}
                          </div>
                        ))}
                        <button 
                          style={{ backgroundColor: previewModal.popup.configuration.button_color || 'var(--theme-primary)' }}
                          className="w-full py-3 text-white rounded-md font-medium"
                          disabled
                        >
                          {previewModal.popup.configuration.button_text || 'Submit'}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* Left Layout */}
                {layout === 'left' && (
                  <div className="flex">
                    {previewModal.popup.configuration.image_url && (
                      <div className={`flex-shrink-0 ${imageFormat === 'square' ? 'w-1/2' : 'w-2/5'}`}>
                        <img 
                          src={previewModal.popup.configuration.image_url} 
                          alt="Popup" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex-1 p-6">
                      <h2 className="text-lg font-bold text-gray-900 mb-3">{previewModal.popup.configuration.title}</h2>
                      {previewModal.popup.configuration.description && (
                        <p className="text-sm text-gray-600 mb-4">{previewModal.popup.configuration.description}</p>
                      )}
                      <div className="space-y-3">
                        {previewModal.popup.configuration.form_fields?.map((field, index) => (
                          <div key={index}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {field.label} {field.required && <span className="text-red-500">*</span>}
                            </label>
                            {field.field_type === 'textarea' ? (
                              <textarea 
                                placeholder={field.placeholder}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                                rows={3}
                                disabled
                              />
                            ) : field.field_type === 'checkbox' ? (
                              <label className="flex items-center">
                                <input type="checkbox" className="mr-2" disabled />
                                <span className="text-sm text-gray-600">{field.placeholder || field.label}</span>
                              </label>
                            ) : (
                              <input 
                                type={field.field_type}
                                placeholder={field.placeholder}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                                disabled
                              />
                            )}
                          </div>
                        ))}
                        <button 
                          style={{ backgroundColor: previewModal.popup.configuration.button_color || 'var(--theme-primary)' }}
                          className="w-full py-3 text-white rounded-md font-medium"
                          disabled
                        >
                          {previewModal.popup.configuration.button_text || 'Submit'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Right Layout */}
                {layout === 'right' && (
                  <div className="flex">
                    <div className="flex-1 p-6">
                      <h2 className="text-lg font-bold text-gray-900 mb-3">{previewModal.popup.configuration.title}</h2>
                      {previewModal.popup.configuration.description && (
                        <p className="text-sm text-gray-600 mb-4">{previewModal.popup.configuration.description}</p>
                      )}
                      <div className="space-y-3">
                        {previewModal.popup.configuration.form_fields?.map((field, index) => (
                          <div key={index}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {field.label} {field.required && <span className="text-red-500">*</span>}
                            </label>
                            {field.field_type === 'textarea' ? (
                              <textarea 
                                placeholder={field.placeholder}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                                rows={3}
                                disabled
                              />
                            ) : field.field_type === 'checkbox' ? (
                              <label className="flex items-center">
                                <input type="checkbox" className="mr-2" disabled />
                                <span className="text-sm text-gray-600">{field.placeholder || field.label}</span>
                              </label>
                            ) : (
                              <input 
                                type={field.field_type}
                                placeholder={field.placeholder}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                                disabled
                              />
                            )}
                          </div>
                        ))}
                        <button 
                          style={{ backgroundColor: previewModal.popup.configuration.button_color || 'var(--theme-primary)' }}
                          className="w-full py-3 text-white rounded-md font-medium"
                          disabled
                        >
                          {previewModal.popup.configuration.button_text || 'Submit'}
                        </button>
                      </div>
                    </div>
                    {previewModal.popup.configuration.image_url && (
                      <div className={`flex-shrink-0 ${imageFormat === 'square' ? 'w-1/2' : 'w-2/5'}`}>
                        <img 
                          src={previewModal.popup.configuration.image_url} 
                          alt="Popup" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Text Only Layout */}
                {layout === 'text_only' && (
                  <div className="p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">{previewModal.popup.configuration.title}</h2>
                    {previewModal.popup.configuration.description && (
                      <p className="text-gray-600 mb-4">{previewModal.popup.configuration.description}</p>
                    )}
                    <div className="space-y-3">
                      {previewModal.popup.configuration.form_fields?.map((field, index) => (
                        <div key={index}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {field.label} {field.required && <span className="text-red-500">*</span>}
                          </label>
                          {field.field_type === 'textarea' ? (
                            <textarea 
                              placeholder={field.placeholder}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                              rows={3}
                              disabled
                            />
                          ) : field.field_type === 'checkbox' ? (
                            <label className="flex items-center">
                              <input type="checkbox" className="mr-2" disabled />
                              <span className="text-sm text-gray-600">{field.placeholder || field.label}</span>
                            </label>
                          ) : (
                            <input 
                              type={field.field_type}
                              placeholder={field.placeholder}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                              disabled
                            />
                          )}
                        </div>
                      ))}
                      <button 
                        style={{ backgroundColor: previewModal.popup.configuration.button_color || 'var(--theme-primary)' }}
                        className="w-full py-3 text-white rounded-md font-medium"
                        disabled
                      >
                        {previewModal.popup.configuration.button_text || 'Submit'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Generating Image Modal - Compact */}
      {showGeneratingImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-xs w-full mx-4">
            <div className="flex flex-col items-center">
              {generatingModalStatus === 'generating' && (
                <>
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mb-3"></div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">Generating...</h3>
                  <p className="text-xs text-gray-600 text-center">
                    Creating your image
                  </p>
                </>
              )}
              
              {generatingModalStatus === 'success' && (
                <>
                  <div className="rounded-full h-10 w-10 bg-green-100 flex items-center justify-center mb-3">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">Success!</h3>
                  <p className="text-xs text-gray-600 text-center">
                    {generatingModalMessage}
                  </p>
                </>
              )}
              
              {generatingModalStatus === 'error' && (
                <>
                  <div className="rounded-full h-10 w-10 bg-red-100 flex items-center justify-center mb-3">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">Error</h3>
                  <p className="text-xs text-gray-600 text-center mb-3">
                    {generatingModalMessage}
                  </p>
                  <button
                    onClick={() => {
                      setShowGeneratingImageModal(false);
                      setGeneratingModalStatus('generating');
                      setGeneratingModalMessage('');
                    }}
                    className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LandingPagePopups;
